import { useTheme } from "../lib/ThemeContext";
import type { TextureType } from "../lib/theme";

// Fractal noise SVG for paper-like texture
const noiseTexture =
  "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")";

// Dot grid pattern
function getDotsPattern(isDark: boolean): string {
  const dotColor = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  return `radial-gradient(circle, ${dotColor} 1px, transparent 1px)`;
}

// Mesh gradient for Pro theme
function getMeshGradient(isDark: boolean): string {
  if (isDark) {
    return `
      radial-gradient(ellipse at 20% 20%, rgba(138,43,226,0.12) 0%, transparent 50%),
      radial-gradient(ellipse at 80% 30%, rgba(59,130,246,0.08) 0%, transparent 50%),
      radial-gradient(ellipse at 40% 80%, rgba(236,72,153,0.08) 0%, transparent 50%),
      radial-gradient(ellipse at 70% 70%, rgba(16,185,129,0.06) 0%, transparent 50%)
    `;
  }
  return `
    radial-gradient(ellipse at 20% 20%, rgba(138,43,226,0.08) 0%, transparent 50%),
    radial-gradient(ellipse at 80% 30%, rgba(59,130,246,0.06) 0%, transparent 50%),
    radial-gradient(ellipse at 40% 80%, rgba(236,72,153,0.06) 0%, transparent 50%)
  `;
}

function getTextureStyles(
  type: TextureType,
  opacity: number,
  isDark: boolean
): React.CSSProperties {
  const baseStyles: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    zIndex: 0,
    pointerEvents: "none",
    opacity,
  };

  switch (type) {
    case "noise":
      return {
        ...baseStyles,
        backgroundImage: noiseTexture,
      };

    case "dots":
      return {
        ...baseStyles,
        backgroundImage: getDotsPattern(isDark),
        backgroundSize: "24px 24px",
      };

    case "mesh":
      return {
        ...baseStyles,
        background: getMeshGradient(isDark),
        backgroundAttachment: "fixed",
      };

    case "none":
    default:
      return {
        ...baseStyles,
        opacity: 0,
      };
  }
}

export function TextureOverlay() {
  const { theme, isDark } = useTheme();
  const { type, opacity } = theme.texture;

  if (type === "none") {
    return null;
  }

  return <div style={getTextureStyles(type, opacity, isDark)} aria-hidden="true" />;
}
